/**
 * use-swipe-reveal.ts — Vuốt ngang một hàng để lộ nút thao tác phía sau (kiểu hàng hội thoại của Lark Mobile).
 *
 * Dùng pointer events + `touch-action: pan-y` trên hàng: trình duyệt vẫn cuộn dọc, cử chỉ ngang mới tới đây.
 * Chỉ MỘT hàng mở một lúc (hàng khác mở → các hàng còn lại tự đóng qua sự kiện cửa sổ). Cú chạm kết thúc sau khi vuốt không
 * được tính là bấm (consumeClick) để không mở nhầm hội thoại.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { clampSwipe, inEdgeZone, settleSwipe, swipeAxis } from '@/lib/gestures'

const OPEN_EVENT = 'ecdp:swipe-row-open'

export function useSwipeReveal({ max, enabled, id }: { max: number; enabled: boolean; id: string }) {
  const [offset, setOffset] = useState(0)
  const [dragging, setDragging] = useState(false)
  const offsetRef = useRef(0)
  const gesture = useRef<{ x: number; y: number; base: number; axis: 'x' | 'y' | null; t: number; lastX: number; lastT: number; pid: number } | null>(null)
  const moved = useRef(false)

  const apply = useCallback((v: number) => { offsetRef.current = v; setOffset(v) }, [])
  const close = useCallback(() => apply(0), [apply])

  // Hàng khác vừa mở → đóng hàng này.
  useEffect(() => {
    const onOther = (e: Event) => { if ((e as CustomEvent<string>).detail !== id) apply(0) }
    window.addEventListener(OPEN_EVENT, onOther)
    return () => window.removeEventListener(OPEN_EVENT, onOther)
  }, [id, apply])

  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!enabled || (e.pointerType === 'mouse' && e.button !== 0)) return
      // Mép trái màn hình dành cho cử chỉ mở ngăn kéo bộ lọc (hooks/use-edge-swipe.ts) — hàng không nhận cú vuốt bắt đầu ở đó
      // (kể cả khi hàng đang mở: đóng bằng cách chạm vào hàng).
      if (e.pointerType !== 'mouse' && inEdgeZone(e.clientX)) return
      moved.current = false
      gesture.current = { x: e.clientX, y: e.clientY, base: offsetRef.current, axis: null, t: e.timeStamp, lastX: e.clientX, lastT: e.timeStamp, pid: e.pointerId }
    },
    onPointerMove: (e: React.PointerEvent) => {
      const g = gesture.current
      if (!g || e.pointerId !== g.pid) return
      const dx = e.clientX - g.x
      const dy = e.clientY - g.y
      if (!g.axis) {
        g.axis = swipeAxis(dx, dy)
        if (g.axis === 'x') {
          setDragging(true)
          try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch { /* bỏ qua */ }
        }
      }
      if (g.axis !== 'x') return
      moved.current = true
      g.lastX = e.clientX
      g.lastT = e.timeStamp
      apply(clampSwipe(g.base, dx, max))
    },
    onPointerUp: (e: React.PointerEvent) => {
      const g = gesture.current
      gesture.current = null
      if (!g || g.axis !== 'x') return
      setDragging(false)
      const dt = Math.max(1, e.timeStamp - g.t)
      const velocity = (e.clientX - g.x) / dt
      const next = settleSwipe(offsetRef.current, max, velocity)
      apply(next)
      if (next !== 0) window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: id }))
    },
    onPointerCancel: () => {
      const g = gesture.current
      gesture.current = null
      if (g?.axis === 'x') { setDragging(false); apply(settleSwipe(offsetRef.current, max)) }
    },
  }

  /** Dùng ở onClick của hàng: true = bỏ qua cú bấm (vừa vuốt xong, hoặc hàng đang mở thì chỉ đóng lại). */
  const consumeClick = useCallback((): boolean => {
    if (moved.current) { moved.current = false; return true }
    if (offsetRef.current !== 0) { apply(0); return true }
    return false
  }, [apply])

  return { offset, dragging, handlers, close, consumeClick, open: offset < 0 }
}
